import qs from "qs";
import {
    advancedSearchPathFragment,
    bundleManagerPathFragment,
    dashboardViewPathFragment,
    dashboardEditPathFragment,
    executionsPathFragment,
    logSubPageKey,
    settingsPathFragment,
    storageAnalysisSubPageKey,
    usersRolesPathFragment,
    workspaceSyncsSubPageKey,
} from "utils/RouterManager/constants";

/** Filter state lives in localStorage — never keep these in the URL. */
export const FILTER_QUERY_KEYS = [
    "filter",
    "customFilter",
    "include_deleted",
    "hide_deleted",
    "tab",
] as const;

export const stripFilterQueryParams = (params: Record<string, string>): void => {
    for (const key of FILTER_QUERY_KEYS) {
        delete params[key];
    }
};

/** Remove filter keys from a search string while preserving route flags (?advanced-search, ?logs, …). */
export const stripFilterFromSearch = (search: string): string => {
    const params = parseSearchParams(search.startsWith("?") ? search.substring(1) : search);
    stripFilterQueryParams(params);
    return formatSearchParams(params);
};

/**
 * Sub-pages whose screen imports `filter=` URL params into its stored filter and then strips
 * them from the URL itself (deep links, e.g. a dashboard "Show all" link to the executions
 * list). Global URL clean-up leaves the filter params of these routes to that screen.
 */
export const FILTER_OWNING_SUB_PAGE_FLAGS = [
    executionsPathFragment,
    advancedSearchPathFragment,
    bundleManagerPathFragment,
    logSubPageKey,
] as const;

export const isFilterOwnedBySubPage = (params: Record<string, string>): boolean =>
    FILTER_OWNING_SUB_PAGE_FLAGS.some((flag) => flag in params);

export const SUB_PAGE_QUERY_FLAGS = [
    executionsPathFragment,
    settingsPathFragment,
    storageAnalysisSubPageKey,
    workspaceSyncsSubPageKey,
    bundleManagerPathFragment,
    logSubPageKey,
    advancedSearchPathFragment,
    usersRolesPathFragment,
    dashboardViewPathFragment,
    dashboardEditPathFragment,
] as const;

const isSubPageFlag = (key: string): boolean =>
    (SUB_PAGE_QUERY_FLAGS as readonly string[]).includes(key);

export const parseSearchParams = (search: string): Record<string, string> => {
    const normalized = search.startsWith("?") ? search.substring(1) : search;
    if (!normalized) {
        return {};
    }
    const parsed = qs.parse(normalized);
    const params: Record<string, string> = {};
    for (const [key, value] of Object.entries(parsed)) {
        if (value == null) {
            params[key] = "";
        } else if (Array.isArray(value)) {
            params[key] = String(value[0] ?? "");
        } else {
            params[key] = String(value);
        }
    }
    return params;
};

/**
 * Serialize query params with stable ordering:
 * 1. id or name (record identifier)
 * 2. sub-page route flags (?executions, ?settings, …) without "=" when empty
 * 3. canvas (when present)
 * 4. remaining filter/search params
 */
export const formatSearchParams = (params: Record<string, string | undefined | null>): string => {
    const parts: string[] = [];

    if (params.id) {
        parts.push(`id=${encodeURIComponent(params.id)}`);
    } else if (params.name) {
        parts.push(`name=${encodeURIComponent(params.name)}`);
    }

    for (const flag of SUB_PAGE_QUERY_FLAGS) {
        if (!(flag in params)) {
            continue;
        }
        const value = params[flag];
        if (value == null || value === "") {
            parts.push(flag);
        } else {
            parts.push(`${flag}=${encodeURIComponent(String(value))}`);
        }
    }

    if ("canvas" in params && params.canvas != null && params.canvas !== "") {
        parts.push(`canvas=${encodeURIComponent(String(params.canvas))}`);
    }

    for (const [key, value] of Object.entries(params)) {
        if (key === "id" || key === "name" || key === "canvas" || isSubPageFlag(key)) {
            continue;
        }
        if (value == null || value === "") {
            continue;
        }
        parts.push(`${key}=${encodeURIComponent(String(value))}`);
    }

    return parts.join("&");
};

/**
 * When rewriting filter/search query params, keep sub-page route flags (?executions,
 * ?settings, ?logs, etc.) so RouterManager still renders the correct screen.
 */
export const copySubPageQueryFlags = (
    target: Record<string, string | undefined>,
    sourceSearch: string
): void => {
    const source = parseSearchParams(sourceSearch);
    for (const flag of SUB_PAGE_QUERY_FLAGS) {
        if (flag in source) {
            target[flag] = source[flag];
        }
    }
    if ("canvas" in source) {
        target.canvas = source.canvas;
    }
};

/** The view-determining flags (the sub-page route flags + canvas) present in a search string. */
const viewFlagsOf = (search: string): Set<string> =>
    new Set(
        Object.keys(parseSearchParams(search)).filter(
            (key) => key === "canvas" || isSubPageFlag(key)
        )
    );

/**
 * True when navigating from `currentSearch` to `nextSearch` stays on the SAME
 * record view — i.e. it is a pure in-page tab switch, not a view navigation.
 *
 * It qualifies when the next URL adds no view-determining flag and the only
 * flag it may drop is ?logs (leaving the logs tab, which runs
 * `replaceSearchParams`). The plain single-record view lands on ?id=X (no view
 * flags); the dashboard config editor keeps its ?edit flag, so a
 * logs→metadata/comments switch there lands on the same ?edit view and must not
 * raise the unsaved-changes prompt. Any other delta — adding a flag, or dropping
 * a non-logs view flag such as ?edit or ?canvas — is a genuine view change.
 *
 * The record identifier (?id/?name) and filter params are ignored here; the
 * caller is responsible for confirming the record itself is unchanged.
 */
export const isInPageTabSwitch = (
    currentSearch: string,
    nextSearch: string
): boolean => {
    const current = viewFlagsOf(currentSearch);
    const next = viewFlagsOf(nextSearch);
    for (const flag of next) {
        if (!current.has(flag)) {
            return false; // next adds a view flag → genuine navigation
        }
    }
    for (const flag of current) {
        if (!next.has(flag) && flag !== logSubPageKey) {
            return false; // next drops a non-logs view flag → genuine navigation
        }
    }
    return true;
};
